import React from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  useMediaQuery,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchAssetInventory, assetTypeList, updateDiscoveredAssets } from "../../redux/actions/assetInventory";

function normalizeHost(value) {
  let host = String(value || "").trim().toLowerCase();
  if (!host) return "";

  if (host.includes("://")) {
    host = host.split("://", 2)[1];
  }
  if (host.includes("/")) {
    host = host.split("/", 1)[0];
  }
  if (host.includes(":")) {
    host = host.split(":", 1)[0];
  }
  if (host.startsWith("*.")) {
    host = host.slice(2);
  }
  if (host.endsWith(".")) {
    host = host.slice(0, -1);
  }

  return host.trim();
}

function isScopedSubdomain(value, rootDomain) {
  const host = normalizeHost(value);
  const root = normalizeHost(rootDomain);

  if (!host || !root || host.includes("@") || host.includes(" ")) {
    return false;
  }

  return host === root || host.endsWith(`.${root}`);
}

function getStoredOrgaName() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.org_name || user?.entreprise || "";
  } catch (_) {
    return "";
  }
}

export default function DiscoveredAssets({ editMode }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  // const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const orgaProfileData = useSelector((state) => state.organizationProfile.organizationMetricsById) || {};
  const validationEnabled =
    typeof orgaProfileData?.validation_status === "boolean"
      ? orgaProfileData.validation_status
      : true;
  const canEdit = orgaProfileData?.assets_discovery_status === "completed" && !orgaProfileData?.assets_discovery_validation;
  const activeEditMode =
    validationEnabled &&
    (canEdit || location.state?.editMode === true || Boolean(editMode));
  const dispatch = useDispatch();
  const orgaId =
    useSelector((state) => state.organizationProfile?.id) ||
    localStorage.getItem("orgaId");

  const data = useSelector((state) => state.assetInventory?.assets_discovery);
  const assetTypes = useSelector((state) => state.assetInventory?.assetTypes || []);

  const [rows, setRows] = useState(Array.isArray(data) ? data : []);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [filterField, setFilterField] = useState("name");
  const [editingId, setEditingId] = useState(null);
  const [editType, setEditType] = useState("");
  const payloadRef = useRef({ ops: [] });
  const payload = payloadRef.current;
  const [opsVersion, setOpsVersion] = useState(0);
  const [editValue, setEditValue] = useState("");
  const [editTypeId, setEditTypeId] = useState(""); // store selected type id
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [lastSubdomainDeleteOpen, setLastSubdomainDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const orgaDomain = useMemo(
    () =>
      normalizeHost(
        orgaProfileData?.name_A ||
          orgaProfileData?.organization_data?.name_A ||
          getStoredOrgaName()
      ),
    [orgaProfileData]
  );

  const activeSubdomainCount = useMemo(
    () => (rows || []).filter((r) => isScopedSubdomain(r?.name, orgaDomain)).length,
    [rows, orgaDomain]
  );

  const filteredRows = useMemo(() => {
    if (!filterQuery.trim()) return rows || [];
    const q = filterQuery.trim().toLowerCase();
    return (rows || []).filter((r) => {
      let value = "";
      if (filterField === "name") {
        value = String(r?.name || "").toLowerCase();
      } else if (filterField === "type") {
        value = String(r?.type || "").toLowerCase();
      } else {
        value = String(r?.associated_asset_group_name || "").toLowerCase();
      }
      return value.includes(q);
    });
  }, [rows, filterQuery, filterField]);

  const groupedRows = useMemo(() => {
    const map = new Map();
    (filteredRows || []).forEach((r) => {
      const key = r?.associated_asset_group_name || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [filteredRows]);

  const addOp = (op) => {
    console.log("[DiscoveredAssets][QUEUE_OP]", op);
    const ops = payloadRef.current.ops || (payloadRef.current.ops = []);
    if (!op) return;

    // Normalize asset_id (string compare safe)
    const opAssetId = op?.where?.asset_id ?? op?.asset?.asset_id;
    const assetId = opAssetId != null ? String(opAssetId) : null;

    // If deleting: remove any pending updates for same asset and keep ONLY one delete
    if (op.op === "delete_asset" && assetId) {
      payloadRef.current.ops = ops.filter((x) => {
        const xId = x?.where?.asset_id ?? x?.asset?.asset_id;
        return String(xId) !== assetId;
      });
      payloadRef.current.ops.push({ op: "delete_asset", where: { asset_id: assetId } });

      setOpsVersion((v) => v + 1);
      return;
    }

    // If update: ignore updates if asset already has a delete op queued
    if (op.op === "update_asset" && assetId) {
      const hasDelete = ops.some(
        (x) => x?.op === "delete_asset" && String(x?.where?.asset_id) === assetId
      );
      if (hasDelete) {
        setOpsVersion((v) => v + 1);
        return; // delete wins
      }

      // Merge with existing update op for same asset (combine changes)
      const idx = ops.findIndex(
        (x) => x?.op === "update_asset" && String(x?.where?.asset_id) === assetId
      );

      if (idx >= 0) {
        ops[idx] = {
          ...ops[idx],
          changes: { ...(ops[idx].changes || {}), ...(op.changes || {}) },
        };
      } else {
        ops.push(op);
      }

      setOpsVersion((v) => v + 1);
      return;
    }

    // Fallback: just push
    ops.push(op);
    setOpsVersion((v) => v + 1);
  };

  const resetForm = () => {
    setType("");
    setName("");
  };

  useEffect(() => {
    dispatch(fetchAssetInventory(orgaId));
  }, [dispatch, orgaId]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const safe = data.filter((r) => r && r.asset_id);
      if (safe.length !== data.length) {
        console.warn("[DiscoveredAssets] Some rows missing asset_id were dropped:", data);
      }
      setRows(safe);
    }
  }, [data]);

  useEffect(() => {
    if (!editingId || !orgaId) return;
    dispatch(assetTypeList(editingId, orgaId));
  }, [dispatch, editingId, orgaId]);

  useEffect(() => {
    if (!Boolean(editingId)) return;
    if (editTypeId) return;
    if (!editType) return;

    const match = assetTypes.find((t) => (t.name || "").trim() === (editType || "").trim());
    if (match?.id != null) setEditTypeId(String(match.id));
  }, [assetTypes, editingId, editTypeId, editType]);


  // const handleAdd = () => {
  //   // basic validation: name required
  //   if (!name.trim()) return;
  //   const newRow = {
  //     asset_id: `manual-${Date.now()}`,
  //     associated_asset_group_name: "",
  //     type: type || "",
  //     name: name || "",
  //     source: "manual",
  //   };
  //   setRows((prev) => [newRow, ...(prev || [])]);
  //   // register op
  //   addOp({ op: "add_asset", asset: newRow });
  //   resetForm();
  //   setOpen(false);
  // };

  const deleteLocally = (id) => {
    if (!id) return;

    console.log("[DiscoveredAssets][OP] delete_asset", {
      asset_id: String(id),
    });

    setRows((prev) => (prev || []).filter((r) => String(r.asset_id) !== String(id)));
    addOp({ op: "delete_asset", where: { asset_id: id } });
  };

  const requestDelete = (id) => {
    if (!id) return;

    const target = (rows || []).find((r) => String(r.asset_id) === String(id));
    if (!isScopedSubdomain(target?.name, orgaDomain)) {
      deleteLocally(id);
      return;
    }

    setPendingDeleteId(id);
    if (activeSubdomainCount <= 1) {
      setLastSubdomainDeleteOpen(true);
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const confirmDeleteSubdomain = () => {
    if (!pendingDeleteId) {
      setDeleteConfirmOpen(false);
      return;
    }

    if (activeSubdomainCount <= 1) {
      setDeleteConfirmOpen(false);
      setLastSubdomainDeleteOpen(true);
      return;
    }

    deleteLocally(pendingDeleteId);
    setPendingDeleteId(null);
    setDeleteConfirmOpen(false);
  };

  const closeDeleteConfirm = () => {
    setPendingDeleteId(null);
    setDeleteConfirmOpen(false);
  };

  const closeLastSubdomainDialog = () => {
    setPendingDeleteId(null);
    setLastSubdomainDeleteOpen(false);
  };


  const openEdit = (row) => {
    const id = row?.asset_id;
    if (!id) {
      console.warn("[DiscoveredAssets] Cannot edit row without asset_id:", row);
      return;
    }
    setEditingId(id);

    setEditTypeId(row.type_id ? String(row.type_id) : "");
    setEditType(row.type || "");
    setEditValue(String(row?.estimated_value ?? row?.value ?? ""));
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditType("");
    setEditTypeId("");
    setEditValue("");
  };

  const saveEdit = () => {
    if (!editingId) return;

    // sanitize numeric value (1..10) or empty
    const parsed = Number(editValue);
    const numericValue =
      editValue === "" || !Number.isFinite(parsed)
        ? null
        : Math.max(1, Math.min(10, parsed));

    setRows((prev) =>
      (prev || []).map((r) => {
        if (r.asset_id !== editingId) return r;

        const next = { ...r, type: editType || "", type_id: editTypeId || "" };

        if (numericValue === null) {
          delete next.estimated_value;
        } else {
          next.estimated_value = numericValue;
        }
        return next;
      })
    );
    // register one update op containing BOTH changes
    const selected = (assetTypes || []).find((t) => String(t.id) === String(editTypeId));
    const canonicalName = selected?.name || editType || "";

    const opPayload = {
      op: "update_asset",
      where: { asset_id: editingId },
      changes: {
        type_id: String(editTypeId || "").trim(),
        type_name: String(canonicalName || "").trim(),
        ...(numericValue === null ? {} : { estimated_value: numericValue }),
      },
    };

    console.log("[DiscoveredAssets][OP] update_asset", {
      asset_id: String(editingId),
      type_id: opPayload.changes.type_id,
      type_name: opPayload.changes.type_name,
      estimated_value:
        "estimated_value" in opPayload.changes
          ? opPayload.changes.estimated_value
          : null,
    });

    addOp(opPayload);
    closeEdit();
  };

  function cleanString(str) {
    // Replace any non-letter/number with a space, then collapse multiple spaces
    return str.replace(/[^a-zA-Z0-9]+/g, " ").trim();
  }

  const logPendingOpsSummary = () => {
    const ops = payloadRef.current.ops || [];

    console.group("[DiscoveredAssets] Pending Ops Summary");

    // 1) All ops (raw)
    console.group("All Ops (raw)");
    console.table(
      ops.map((op, idx) => ({
        idx,
        op: op.op,
        asset_id: op?.where?.asset_id ?? op?.asset?.asset_id ?? "",
        type_id: op?.changes?.type_id ?? "",
        type_name: op?.changes?.type_name ?? "",
        estimated_value:
          typeof op?.changes?.estimated_value === "number"
            ? op.changes.estimated_value
            : "",
      }))
    );
    console.groupEnd();

    // 2) Deletes only
    const deletes = ops.filter((o) => o.op === "delete_asset");
    console.group(`delete_asset (${deletes.length})`);
    console.table(
      deletes.map((op, idx) => ({
        idx,
        asset_id: op?.where?.asset_id ?? "",
      }))
    );
    console.groupEnd();

    // 3) Updates only
    const updates = ops.filter((o) => o.op === "update_asset");
    console.group(`update_asset (${updates.length})`);
    console.table(
      updates.map((op, idx) => ({
        idx,
        asset_id: op?.where?.asset_id ?? "",
        type_id: op?.changes?.type_id ?? "",
        type_name: op?.changes?.type_name ?? "",
        estimated_value:
          typeof op?.changes?.estimated_value === "number"
            ? op.changes.estimated_value
            : "",
      }))
    );
    console.groupEnd();

    console.groupEnd();
  };


  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant={isSm ? "h6" : "h5"} sx={{ fontWeight: 700 }}>
          {t("asset_inventory")}
        </Typography>

        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            select
            label={t("filter")}
            value={filterField}
            onChange={(e) => setFilterField(e.target.value)}
            size={isSm ? "small" : "medium"}
            fullWidth={isSm}
            sx={{ minWidth: isSm ? "100%" : 180 }}
          >
            <MenuItem value="name">{t("name")}</MenuItem>
            <MenuItem value="type">{t("type")}</MenuItem>
            <MenuItem value="group">{t("group")}</MenuItem>
          </TextField>

          <TextField
            label={t("filter") || "Filter"}
            placeholder={`${t("filter_by") || "Filter by"} ${t(filterField)}`}
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            size={isSm ? "small" : "medium"}
            fullWidth={isSm}
            sx={{ minWidth: isSm ? "100%" : 300 }}
          />
        </Box>

        {/* {activeEditMode && (
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            size={isSm ? "small" : "medium"}
          >
            Add asset
          </Button>
        )} */}
      </Box>

      {!isSm ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 600, width: "100%", overflowX: "auto" }}
        >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", sm: "0.95rem", md: "0.98rem" },
                  }}
                >
                  {t("name")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", sm: "0.95rem", md: "0.98rem" },
                  }}
                >
                  {t("type")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "0.98rem" },
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  {t("group")}
                </TableCell>
                {/* <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "0.98rem" },
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  {t("source")}
                </TableCell> */}

                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem", md: "0.98rem" },
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Estimated value (/10)
                </TableCell>

                {activeEditMode && (
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.85rem", sm: "0.95rem", md: "0.98rem" },
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r) => (
                <TableRow key={r.asset_id} hover>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.88rem", sm: "0.9rem", md: "0.95rem" },
                    }}
                  >
                    {r.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.88rem", sm: "0.9rem", md: "0.95rem" },
                    }}
                  >
                    {r.type}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.95rem" },
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {r.associated_asset_group_name}
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.95rem" },
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {cleanString(r.source)}
                  </TableCell> */}
                  <TableCell
                    sx={{
                      fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.95rem" },
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {r?.estimated_value ?? r?.value ?? "-"}
                  </TableCell>
                  {activeEditMode && (
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openEdit(r)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      {/* {r.source === "manual" ? (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => requestDelete(r.asset_id)}
                        >
                          Delete
                        </Button>
                      ) : null} */}
                      <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => requestDelete(r.asset_id)}
                        >
                          Delete
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length > 0 && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={activeEditMode ? 5 : 4} align="center">
                    No matching assets
                  </TableCell>
                </TableRow>
              )}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={activeEditMode ? 5 : 4} align="center">
                    No discovered assets
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ width: "100%" }}>
          {rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>No discovered assets</Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>No matching assets</Box>
          ) : (
            groupedRows.map(([groupName, items], idx) => (
              <Box key={groupName || `group-${idx}`} sx={{ mb: 2 }}>
                {idx > 0 && <Divider sx={{ my: 2 }} />}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {t("group")}: {groupName || "-"}
                </Typography>
                {items.map((r) => (
                  <Paper
                    key={r.asset_id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {r.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{t("group")}:</strong>{" "}
                        {r.associated_asset_group_name || groupName || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("type")}:</strong> {r.type || "-"}
                      </Typography>
                      {/* <Typography variant="body2">
                        <strong>{t("source")}:</strong>{" "}
                        {cleanString(r.source) || "-"}
                      </Typography> */}
                      <Typography
                          variant="body2"
                          sx={{
                            flexBasis: { xs: "100%", sm: "auto" }, // 👈 forces new line on mobile
                            width: { xs: "100%", sm: "auto" },    // (extra safety)
                          }}
                        >
                          <strong>Estimated value:</strong>&nbsp;
                          {r?.estimated_value ?? r?.value ?? "-"}
                        </Typography>

                    </Box>
                    {activeEditMode && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openEdit(r)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        {/* {r.source === "manual" ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => requestDelete(r.asset_id)}
                          >
                            Delete
                          </Button>
                        ) : null} */}
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => requestDelete(r.asset_id)}
                          >
                            Delete
                          </Button>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ))
          )}
        </Box>
      )}

      {activeEditMode && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, mt: 2 }}>
          {" "}
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!payload?.ops?.length}
            onClick={() => {
              logPendingOpsSummary();

              dispatch(updateDiscoveredAssets(orgaId, payloadRef.current.ops)).then((res) => {
                if (res) {
                  payloadRef.current.ops = [];
                  setOpsVersion((v) => v + 1);
                }
              });
            }}
          >
            Save Changes ({payload?.ops?.length || 0})
          </Button>
        </Box>
      )}

      <Dialog
          open={Boolean(editingId)}
          onClose={closeEdit}
          maxWidth="xs"
          fullWidth
          fullScreen={isSm}
        >
          <DialogTitle>Edit Asset</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {/* Existing Type field */}
              <TextField
                select
                label="Type"
                value={editTypeId}
                onChange={(e) => {
                  const id = e.target.value;
                  setEditTypeId(id);

                  // also set editType string so your UI/table updates immediately
                  const selected = (assetTypes || []).find((t) => String(t.id) === String(id));
                  setEditType(selected?.name || "");
                }}
                fullWidth
                size="small"
                disabled={!assetTypes.length}
              >
                {(assetTypes || []).map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </MenuItem>
                ))}
              </TextField>


              {/* New Estimated Value field */}
              <TextField
                label="Estimated value (/10)"
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                fullWidth
                size="small"
                inputProps={{
                  min: 1,
                  max: 10,
                  step: 1,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEdit}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveEdit}
              disabled={(!editTypeId || !String(editTypeId).trim()) && (editValue === "" || editValue == null)}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove subdomain?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Deleting this subdomain will exclude it from the vulnerability scan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} size="small">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteSubdomain}
            color="error"
            variant="contained"
            size="small"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lastSubdomainDeleteOpen}
        onClose={closeLastSubdomainDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <IconButton
            aria-label="close"
            onClick={closeLastSubdomainDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 3 }}>
            The vulnerability scan requires at least one active subdomain.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isSm}
      >
        <DialogTitle>Add asset</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              fullWidth
              size="small"
            >
              {[
                "External Strategic Partnerships",
                "Infrastructure-as-a-Service Providers",
                "Managed Service Providers",
                "Platform-as-a-Service Providers",
                "Software-as-a-Service Providers",
              ].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
            /> */}
            {/* source is fixed to 'manual' and not shown in the form */}
          {/* </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!name.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  );
}
